import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import type { RootState } from '../../../redux/rootReducer';
import type { AppDispatch } from '../../../redux/store';
import {
  removeEngagement,
  deriveAdvisoryStage,
  mapAdvisoryErrorCode,
  startEngagementListeners,
  stopEngagementListeners,
} from '../../../redux/slices/advisory';
import type {
  EngagementSummaryDoc,
  StrategicAdvisoryDoc,
} from '../../../redux/slices/advisory/advisoryTypes';
import { deleteEngagement, extractErrorCode } from '../../../services/advisoryService';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { formatRelativeDateTime } from '../../../utils/dateUtils';
import { stageStatusLine, truncateGoalTitle } from '../advisoryPresentation';
import { row, rowText, rowTitle, meta, focusRing, pressable } from '../../../styles/visualLanguage';

/**
 * A plain object literal (not a new abstraction) that pads an
 * `EngagementSummaryDoc` into the exact shape `deriveAdvisoryStage` already
 * accepts, so the list row and the detail route can never disagree about
 * how a document's stage is derived.
 */
export function toStrategicAdvisoryDoc(engagement: EngagementSummaryDoc): StrategicAdvisoryDoc {
  return {
    status: engagement.status,
    goal: engagement.goal,
    maxIterations: engagement.maxIterations,
    canRefine: engagement.canRefine,
    updatedAt: engagement.updatedAt,
  };
}

/**
 * EngagementRow — one real-engagement row on `/advisory`.
 *
 * Owns its own navigation, dispatch and user-id selector, matching this
 * repo's convention: no component here takes a navigation callback prop
 * (see `NewEngagementRow.tsx`, `History.tsx`).
 *
 * Stage is derived by feeding the literal channel state `'ready'` — this row
 * only exists because the engagements channel already delivered a
 * successful snapshot, so channel health must never be re-derived per row —
 * into the UNCHANGED `deriveAdvisoryStage`. No second, list-specific stage
 * derivation is introduced here.
 *
 * Delete affordance: mirrors `ConversationMenu.tsx` + `History.tsx`'s own
 * chat-deletion shape — a small icon button opens a one-item menu, which
 * opens a confirm dialog, and only the dialog's confirm button actually
 * deletes. `deleteEngagement` (`advisoryService.ts`) is the one write call;
 * this component never touches Firestore directly. A rejection is never
 * swallowed: `extractErrorCode` reads the code off it and
 * `mapAdvisoryErrorCode` turns that into one of this feature's own
 * pre-authored i18n keys, shown inline in the still-open dialog — the raw
 * provider message never reaches render. After confirmed deletion, the row is
 * removed from the list, project listeners stop, and its cached detail is cleared.
 */
export function EngagementRow({ engagement }: { engagement: EngagementSummaryDoc }) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector((state: RootState) => state.user.user?.id);
  const { t } = useUntypedTranslation();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrorI18nKey, setDeleteErrorI18nKey] = useState<string | null>(null);

  const stage = deriveAdvisoryStage('ready', toStrategicAdvisoryDoc(engagement));

  // A unified draft has no goal attempt yet; retain its original request as its title.
  const title =
    truncateGoalTitle(engagement.goal, engagement.originalQuery) ??
    t('advisory:stage.titlePlaceholder');

  const statusLine = stageStatusLine(stage);
  const status = statusLine ? t(statusLine.key, statusLine.options) : null;

  // The helper's parameter is `string`, not `string | null` — guard before
  // calling it rather than loosening its shared signature.
  const timestamp = engagement.updatedAt ? formatRelativeDateTime(engagement.updatedAt) : null;

  const handleClick = () => {
    if (userId) {
      dispatch(startEngagementListeners({ userId, projectId: engagement.id }));
    }
    navigate(`/advisory/${engagement.id}`);
  };

  // Every handler below stops propagation: the menu trigger is a real
  // descendant of the row's own ListItemButton (nested inside
  // ListItemText's `primary`, mirroring ConversationMenu's placement inside
  // History.tsx's row), so an unstopped click would bubble into
  // `handleClick` above and navigate into the very engagement being
  // deleted.
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleDeleteMenuItemClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
    setDeleteErrorI18nKey(null);
    setConfirmOpen(true);
  };

  const handleCancelDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setConfirmOpen(false);
    setDeleteErrorI18nKey(null);
  };

  // Only remove the row after the backend confirms the persisted deleted status.
  // Rejections keep the dialog open and leave the engagement available for retry.
  const handleConfirmDelete = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!userId || deleting) {
      return;
    }
    setDeleting(true);
    setDeleteErrorI18nKey(null);
    try {
      await deleteEngagement(userId, engagement.id);
      setDeleting(false);
      setConfirmOpen(false);
      dispatch(removeEngagement(engagement.id));
      dispatch(stopEngagementListeners({ userId, projectId: engagement.id }));
      if (location.pathname === `/advisory/${engagement.id}`) {
        navigate('/advisory', { replace: true });
      }
    } catch (error) {
      setDeleting(false);
      setDeleteErrorI18nKey(mapAdvisoryErrorCode(extractErrorCode(error)).i18nKey);
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        aria-label={
          timestamp ? t('advisory:list.row.ariaLabel', { title, status, timestamp }) : undefined
        }
        disableRipple
        disableGutters
        data-testid="engagement-row"
        sx={[row, focusRing, pressable]}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography
                variant="body1"
                noWrap
                component="div"
                sx={[rowTitle, { flexGrow: 1, paddingRight: 1 }]}
              >
                {title}
              </Typography>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                aria-label={t('advisory:list.row.deleteMenuAriaLabel')}
                data-testid="engagement-delete-menu-button"
                sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={[rowText]}
          secondary={
            /*
            Inline text flow, deliberately NOT `display: flex`. As a flex row
            the status and the timestamp were rigid siblings: below ~420px the
            long `awaiting_confirmation` status wrapped *inside* its own span
            while the separator and timestamp stayed pinned on the flex line,
            stranding the `·` mid-column with a ragged gap beside it. As inline
            spans the three parts wrap as one continuous sentence the way the
            prototype's single `.m` string does (`flow/flow-app.js:105`).

            `component="span"` + `display: block` rather than a bare `Box`:
            MUI renders `ListItemText`'s `secondary` inside a `<p>`, and a
            `<div>` there is invalid nesting that browsers silently repair by
            closing the paragraph early.

            `mx: 1` on the separator reproduces the previous `gap: 1` spacing
            exactly (8px either side), so nothing moves at desktop widths.
          */
            <Box component="span" sx={{ display: 'block' }}>
              {status ? (
                <Typography component="span" variant="body2" sx={[meta]}>
                  {status}
                </Typography>
              ) : null}
              {timestamp ? (
                <>
                  <Typography component="span" variant="body2" sx={[meta, { mx: 1 }]}>
                    &middot;
                  </Typography>
                  <Typography component="span" variant="body2" sx={[meta]}>
                    {timestamp}
                  </Typography>
                </>
              ) : null}
            </Box>
          }
        />
      </ListItemButton>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={event => event.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={handleDeleteMenuItemClick}
          sx={{ color: 'error.main' }}
          data-testid="engagement-delete-menu-item"
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('advisory:list.row.delete')}
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby={`engagement-delete-dialog-title-${engagement.id}`}
        aria-describedby={`engagement-delete-dialog-description-${engagement.id}`}
        onClick={event => event.stopPropagation()}
      >
        <DialogTitle id={`engagement-delete-dialog-title-${engagement.id}`}>
          {t('advisory:list.row.deleteConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id={`engagement-delete-dialog-description-${engagement.id}`}
            component="div"
          >
            {t('advisory:list.row.deleteConfirmMessage')}
          </DialogContentText>
          {deleteErrorI18nKey ? (
            <Typography
              variant="body2"
              role="alert"
              sx={{ color: 'error.main', mt: 2 }}
              data-testid="engagement-delete-error"
            >
              {t(deleteErrorI18nKey)}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary" disabled={deleting}>
            {t('advisory:list.row.deleteCancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={deleting} autoFocus>
            {deleting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {t('advisory:list.row.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EngagementRow;
